import { type AllHTMLAttributes } from "react";
import { useNavigate } from "react-router";
import { type To } from "react-router-dom";

import { BrowserTab } from "../../app-utils/browser-tab.js";

interface IRouterLinkProps extends AllHTMLAttributes<HTMLDivElement> {
  to: To;
  openExternally?: boolean;
  forwardedRef?: any;
}

/**
 * This component replaces react-router-dom's <Link> component since it is translated to an anchor dom node (<a>) and
 * it is a bad practice to use <a> as HOC component since it can contain other anchors.
 * Use this for high level linking.
 * Another alternative is <LinkContainer>: it is specific to Bootstrap integration and allows NavLink props in addition.
 */
function RouterLink(props: IRouterLinkProps): JSX.Element {
  const navigate = useNavigate();

  const handleRouting = (event: MouseEvent): void => {
    if (!props.to) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    /** CAUTION
     valid:   "organism/34/genome?viewid=123"
     valid:  { pathname: "organism/34/genome" }
     valid:  { pathname: "organism/34/genome", search: "viewid=123" }
     INVALID: { pathname: "organism/34/genome?viewid=123" }
     *
     */
    const location: To = typeof props.to === "string" ? { pathname: props.to } : props.to;

    if (props.openExternally || event.ctrlKey || event.metaKey || event.button === 1) {
      // open in new tab
      window.open(BrowserTab.createUrl(location));
    } else {
      // redirect to new target using Router's Navigate
      navigate(location);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { forwardedRef, children, openExternally, to, ...htmlProps } = props;

  return (
    <div
      {...htmlProps}
      ref={props.forwardedRef}
      onClick={(e) => {
        htmlProps.onClick && htmlProps.onClick(e);
        handleRouting(e);
      }}
    >
      {props.children}
    </div>
  );
}

export { RouterLink };
